import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, img } from "@/lib/api";

const EMPTY = { name: "", origin: "", category: "saree", price: "", sale_price: "", description: "", image_url: "", is_new: true };
const fc = "bg-[#0B0907] border-[#D4AF37]/20";

export function ProductForm({ open, onOpenChange, initial, isAdmin, onSaved }) {
  const [f, setF] = useState(initial ? { ...initial, sale_price: initial.sale_price ?? "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const ch = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, price: Number(f.price), sale_price: f.sale_price === "" ? null : Number(f.sale_price) };
    setBusy(true);
    try {
      if (initial) {
        const { id, status, created_at, created_by, created_by_name, ...rest } = body;
        await api.patch(`/portal/products/${initial.id}`, { ...rest, clear_sale: body.sale_price === null });
      } else {
        await api.post("/portal/products", body);
      }
      toast.success(initial ? "Saved" : isAdmin ? "Saree added to the store" : "Sent to admin for approval");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14110E] border-[#D4AF37]/25 text-[#F5EFE6] max-h-[90vh] overflow-y-auto scrollbar-thin" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-wide">{initial ? "Edit item" : "Add a new item"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Input required placeholder="Name" value={f.name} onChange={ch("name")} className={fc} data-testid="product-form-name" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.category} onChange={ch("category")} className={`h-9 rounded-md border px-3 text-sm ${fc}`} data-testid="product-form-category">
              <option value="saree">Saree</option>
              <option value="apparel">Other (t-shirts etc.)</option>
            </select>
            <Input placeholder="Origin e.g. Mysore" value={f.origin} onChange={ch("origin")} className={fc} data-testid="product-form-origin" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input required type="number" min="1" placeholder="Price ₹" value={f.price} onChange={ch("price")} className={fc} data-testid="product-form-price" />
            {isAdmin && <Input type="number" min="1" placeholder="Sale price ₹ (clearance)" value={f.sale_price} onChange={ch("sale_price")} className={fc} data-testid="product-form-sale-price" />}
          </div>
          <Textarea placeholder="Description" value={f.description} onChange={ch("description")} className={`${fc} min-h-24`} data-testid="product-form-description" />
          <div className="flex items-center gap-3">
            {f.image_url && <img src={img(f.image_url)} alt="" className="h-16 w-14 object-cover rounded" data-testid="product-form-preview" />}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className={`w-full flex items-center justify-center gap-2 rounded-md border border-dashed border-[#D4AF37]/30 py-3 text-sm text-[#A89F91] hover:border-[#D4AF37]/60 hover:text-[#F3E5AB] transition-colors ${uploading ? "opacity-50" : ""}`}
            data-testid="product-form-upload-btn"
          >
            <Upload className="h-4 w-4" />
            {uploading ? "Uploading..." : f.image_url ? "Change image" : "Upload image from computer"}
          </button>
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#D4AF37]/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#14110E] px-2 text-[#A89F91]/60">or paste URL</span></div>
          </div>
          <Input placeholder="Paste image URL" value={f.image_url} onChange={ch("image_url")} className={fc} data-testid="product-form-image-url" />
          <label className="flex items-center gap-3 text-sm text-[#A89F91]">
            <Switch checked={f.is_new} onCheckedChange={(v) => setF({ ...f, is_new: v })} data-testid="product-form-is-new" /> Mark as fresh arrival
          </label>
          <button disabled={busy} className="btn-glow w-full rounded-full bg-[#D4AF37] py-3 font-semibold text-sm text-[#0B0907] hover:bg-[#F3E5AB] disabled:opacity-50" data-testid="product-form-submit">
            {initial ? "Save changes" : isAdmin ? "Add to store" : "Submit for approval"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
